import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { AsyncTaskLogInfoDto } from '@swagger/humanresources';
import {
  applyLogs,
  isRunning,
  PLANNING_TASK_TYPE,
  PlanningTask,
} from '@features/planning/util-planning-task';
import { PlanningTaskState } from './planning-task-state.interface';
import { toPlanningTask } from './planning-task.mapper';
import { PlanningTaskClient } from './planning-task.client';

const initialState: PlanningTaskState = { tasks: {} };

/**
 * The planning tasks the app follows (#5182). Root-provided on purpose: a task
 * outlives the dialog that started it and the planning page the user may leave.
 */
export const PlanningTaskStore = signalStore(
  { providedIn: 'root' },
  withDevtools('planning-task-store'),
  withState(initialState),
  withProps(() => ({
    _client: inject(PlanningTaskClient),
  })),
  withComputed((store) => ({
    taskList: computed((): PlanningTask[] => Object.values(store.tasks())),
    runningTasks: computed((): PlanningTask[] => Object.values(store.tasks()).filter(isRunning)),
  })),
  withMethods((store) => ({
    track(task: PlanningTask): void {
      patchState(store, { tasks: { ...store.tasks(), [task.asyncTaskUId]: task } });
    },

    /** Applies the logs to a tracked task and returns it; an unknown task returns null. */
    applyLogs(asyncTaskUId: string, logs: AsyncTaskLogInfoDto[]): PlanningTask | null {
      const task = store.tasks()[asyncTaskUId];
      if (!task) return null;
      const updated = applyLogs(task, logs);
      patchState(store, { tasks: { ...store.tasks(), [asyncTaskUId]: updated } });
      return updated;
    },
  })),
  withMethods((store) => ({
    /** Starts finalizing a schedule group and tracks the task the backend created. */
    async startFinalize(scheduleGroupUId: string, planName: string): Promise<PlanningTask> {
      const started = await store._client.startFinalize(scheduleGroupUId);
      const task = toPlanningTask(started, { planName, scheduleGroupUId });
      store.track(task);
      return task;
    },

    /** Reads the logs since the newest one seen and applies them; a finished task needs none. */
    async catchUpLogs(asyncTaskUId: string): Promise<PlanningTask | null> {
      const task = store.tasks()[asyncTaskUId];
      if (!task || !isRunning(task)) return task ?? null;
      const logs = await store._client.fetchLogs(asyncTaskUId, task.lastLogCreated);
      return store.applyLogs(asyncTaskUId, logs);
    },

    /** Tracks the user's running finalize tasks that are not tracked yet, e.g. after a reload (#5182). */
    async trackRunningTasks(): Promise<PlanningTask[]> {
      const running = await store._client.listRunningTasks(PLANNING_TASK_TYPE.finalize);
      const added = running
        .filter((dto) => !store.tasks()[dto.uId])
        .map((dto) => toPlanningTask(dto));
      added.forEach((task) => store.track(task));
      return added;
    },
  })),
);

export type PlanningTaskStoreType = InstanceType<typeof PlanningTaskStore>;
